import { useEffect, useMemo } from 'react'
import { Outlet, useParams } from 'react-router'
import { useShallow } from 'zustand/react/shallow'

import { BreadcrumbSetter } from '@/components/Breadcrumbs'
import { DataCard } from '@/components/card'
import { LicenseFormButton } from '@/components/forms/LicenseForm'
import { ImportLicensesFormButton } from '@/components/forms/LicenseForm/LicenseForm'
import {
  CheckboxDeactivateSelectedStreamingOperatorLicenseItem,
  CheckboxMenu
} from '@/components/table/checkbox'
import { TitleSetter } from '@/components/Title'
import { STORE_STATUS } from '@/constants/store'
import { ROUTE_PARAM } from '@/router/constants'
import { createBulkHandler } from '@/service/bulkOperationsService'
import { streamingRestApi } from '@/service/streamingApi'
import { useOperatorLicensesStoreDGE } from '@/stores/operator/licenses.dge.store'
import { useOperatorStoreDGE } from '@/stores/operator/operator.dge.store'

import { LICENSE_STATUSES, LicenseRow } from './Licenses/licenses.dge.table'
import { DGE_OPERATOR_LICENSES_TABLE_ID } from './Licenses/LicensesView.dge'
import { getOperatorHeaderItems } from './operatorView.dge.table'

export const OperatorViewDGE = () => {
  const { operatorId } = useParams()
  const numericOperatorId = operatorId ? Number(operatorId) : undefined

  const { data, fetchData, resetState } = useOperatorStoreDGE(
    useShallow((state) => ({
      data: state.data,
      fetchData: state.fetchData,
      resetState: state.resetState
    }))
  )

  const { fetchOperatorLicenses, operatorLicenses, isLoading } = useOperatorLicensesStoreDGE(
    useShallow((state) => ({
      fetchOperatorLicenses: state.fetchData,
      isLoading: state.status === STORE_STATUS.loading,
      operatorLicenses: state.data
    }))
  )

  useEffect(() => {
    if (!numericOperatorId) return
    fetchData({ operatorId: numericOperatorId })

    return () => {
      resetState()
    }
  }, [numericOperatorId])

  const operator = useMemo(
    () => (Array.isArray(data) && data.length > 0 && numericOperatorId ? data[0] : null),
    [data, numericOperatorId]
  )

  const title = operator ? `Operator Licenses - ${operator.name}` : 'Operator'

  const handleDeactiveSelectedStreamingLicenses = createBulkHandler({
    name: 'Deactivate Licenses',
    operation: async (license: LicenseRow) => {
      if (license.status.type !== LICENSE_STATUSES.DEACTIVATED) {
        await streamingRestApi.endpoints.operators.deleteLicense(license.id)
      }
    },
    refreshData: () => fetchOperatorLicenses({ operatorId: numericOperatorId! }),
    getItemDisplayName: (license) => license.propertyName || `ID: ${license.id}`
  })

  return (
    <>
      <TitleSetter title={title} />
      <BreadcrumbSetter
        param={ROUTE_PARAM.OPERATOR_ID}
        label={operator ? operator.name : ''}
        isLoading={isLoading}
      />
      <DataCard
        title={title}
        details={getOperatorHeaderItems(operator)}
        headerPostfixActions={
          operator ? (
            <>
              <CheckboxMenu tableId={DGE_OPERATOR_LICENSES_TABLE_ID}>
                <CheckboxDeactivateSelectedStreamingOperatorLicenseItem
                  onDeactivate={handleDeactiveSelectedStreamingLicenses}
                />
              </CheckboxMenu>
              {operator ? (
                <ImportLicensesFormButton.DGE
                  onSubmit={() => fetchOperatorLicenses({ operatorId: operator.id })}
                  data={{ operatorId: operator.id }}
                />
              ) : null}
              {operator ? (
                <LicenseFormButton.DGE
                  onSubmit={() => fetchOperatorLicenses({ operatorId: operator.id })}
                  data={{ operatorId: operator.id, operatorLicenses }}
                />
              ) : null}
            </>
          ) : null
        }
      >
        <Outlet />
      </DataCard>
    </>
  )
}
